import React, { useState, useMemo, useEffect, useCallback } from 'react';
import './KioskShop.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function formatPrice(n) {
  return '$' + (n || 0).toFixed(2);
}

function extractBrand(name) {
  if (!name) return { brand: null, productName: name };
  // Pattern 1: "BRAND - Product Name"
  const dashMatch = name.match(/^([A-Z][A-Z\s&.]+?)\s*[-–—]\s*(.+)$/);
  if (dashMatch) return { brand: dashMatch[1].trim(), productName: dashMatch[2].trim() };
  // Pattern 2: "ALLCAPS Product Name" (e.g., "AVRY Shea Butter Lotion")
  const capsMatch = name.match(/^([A-Z]{2,}(?:\s[A-Z]{2,})*)\s+([A-Z][a-z].+)$/);
  if (capsMatch) return { brand: capsMatch[1].trim(), productName: capsMatch[2].trim() };
  return { brand: null, productName: name };
}

function getImageUrl(image) {
  if (!image) return null;
  if (image.startsWith('http')) return image;
  return `${API_URL}${image}`;
}

// Detect "Brand - Color - Size" variant products (e.g. huha underwear)
// Returns { variantGroups: Map<groupKey, { brand, color, image, price, variants }>, standaloneIds: Set }
function groupVariantProducts(products) {
  const variantPattern = /^(.+?)\s*[-–—]\s*(.+?)\s*[-–—]\s*(Extra Small|Small|Medium|Large|XS|S|M|L|XL|XXL)$/i;
  const groups = new Map();
  const variantIds = new Set();

  for (const p of products) {
    const match = p.name.match(variantPattern);
    if (!match) continue;
    const brandPart = match[1].trim();
    const color = match[2].trim();
    const size = match[3].trim();
    const key = `${brandPart}---${color}`;
    if (!groups.has(key)) {
      groups.set(key, { brand: brandPart, color, image: p.image, price: p.price, variants: [] });
    }
    groups.get(key).variants.push({ id: p.id, size, product: p });
    variantIds.add(p.id);
  }

  // Sort variants by size order
  const sizeOrder = { 'extra small': 0, 'xs': 0, 'small': 1, 's': 1, 'medium': 2, 'm': 2, 'large': 3, 'l': 3, 'xl': 4, 'xxl': 5 };
  for (const group of groups.values()) {
    group.variants.sort((a, b) => (sizeOrder[a.size.toLowerCase()] ?? 99) - (sizeOrder[b.size.toLowerCase()] ?? 99));
  }

  return { variantGroups: groups, variantIds };
}

export default function KioskShop({
  mode, // 'products' | 'packages'
  products = [],
  packages = [],
  cart = [],
  onCartAdd,
  onCartRemove,
  onProductRemove, // remove a standalone product from the invoice (looks up the line + DELETEs)
  onClose,
  loading = false
}) {
  // Variant size picker stays — customer needs it to pick a size for huha-style products.
  // The old standalone-product ProductDetail popup was removed (no useful info, just clutter);
  // standalone cards now tap-to-toggle directly.
  const [selectedVariantGroup, setSelectedVariantGroup] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  // Push history state when shop opens so browser back closes overlays instead of navigating away
  useEffect(() => {
    window.history.pushState({ kioskShop: true }, '');
  }, []);

  // Handle browser/device back button — close the variant size picker first, then close shop
  const handlePopState = useCallback((e) => {
    if (selectedVariantGroup) {
      e.preventDefault();
      setSelectedVariantGroup(null);
      window.history.pushState({ kioskShop: true }, '');
    } else {
      onClose();
    }
  }, [selectedVariantGroup, onClose]);

  // Tap handler for standalone (non-variant) product cards. If already in bag, remove via
  // the invoice-line DELETE path; else add via the normal cart-add (routes products through
  // addProductToVisit → PUT to the service invoice).
  function toggleProduct(p) {
    if (isInBag(p.id)) {
      if (onProductRemove) onProductRemove(p.id);
      return;
    }
    if (p.soldOut) return; // sold out per Zenoti stock mirror — can't be added
    onCartAdd({ id: p.id, name: p.name, price: p.price, type: 'product', image: p.image || null });
  }

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handlePopState]);

  const isInBag = (id) => cart.some(c => c.id === id);

  // Show all products. Sold-out items (soldOut from the Zenoti stock mirror) stay
  // visible but render greyed with a "Sold out" badge and can't be added — see
  // toggleProduct and the card rendering below.
  const inStockProducts = useMemo(() => products, [products]);

  // Group variant products (e.g. huha underwear by color)
  const { variantGroups, variantIds } = useMemo(() => groupVariantProducts(inStockProducts), [inStockProducts]);

  // Group products by category
  const categoryGroups = useMemo(() => {
    if (mode !== 'products' || !inStockProducts.length) return [];
    const groups = {};
    for (const p of inStockProducts) {
      const cat = p.subCategoryName || p.categoryName || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    }
    // Sort categories alphabetically, "Other" last
    return Object.entries(groups).sort((a, b) => {
      if (a[0] === 'Other') return 1;
      if (b[0] === 'Other') return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [mode, inStockProducts]);

  // Curated lists (from Zenoti tags)
  const bestSellers = useMemo(() => inStockProducts.filter(p => p.isBestSeller), [inStockProducts]);
  const seasonalFavourites = useMemo(() => inStockProducts.filter(p => p.isSeasonal), [inStockProducts]);
  const newProducts = useMemo(() => inStockProducts.filter(p => p.isNew), [inStockProducts]);
  const staffPicks = useMemo(() => inStockProducts.filter(p => p.isStaffPick), [inStockProducts]);

  // Extract unique brands (prefer brandName from API, fallback to name parsing)
  const brands = useMemo(() => {
    const brandSet = new Set();
    for (const p of inStockProducts) {
      if (p.brandName) { brandSet.add(p.brandName); continue; }
      const { brand } = extractBrand(p.name);
      if (brand) brandSet.add(brand);
    }
    return [...brandSet].sort();
  }, [inStockProducts]);

  const [selectedBrand, setSelectedBrand] = useState('');

  // Tab structure: All, Best Sellers, By Brand, Seasonal, categories...
  const categoryNames = useMemo(() => {
    const tabs = ['All'];
    if (bestSellers.length > 0) tabs.push('Best Sellers');
    if (brands.length > 0) tabs.push('By Brand');
    if (seasonalFavourites.length > 0) tabs.push('Seasonal Favourites');
    tabs.push(...categoryGroups.map(([name]) => name));
    return tabs;
  }, [categoryGroups, bestSellers, seasonalFavourites, brands]);

  // Filtered products based on active tab
  const filteredGroups = useMemo(() => {
    if (activeCategory === 'All') {
      const sorted = [...inStockProducts].sort((a, b) => a.name.localeCompare(b.name));
      return [['', sorted]];
    }
    if (activeCategory === 'Best Sellers') return [['Best Sellers', bestSellers]];
    if (activeCategory === 'Seasonal Favourites') return [['Seasonal Favourites', seasonalFavourites]];
    if (activeCategory === 'By Brand') {
      if (!selectedBrand) return [['All Products', inStockProducts]];
      const filtered = inStockProducts.filter(p => {
        if (p.brandName) return p.brandName === selectedBrand;
        const { brand } = extractBrand(p.name);
        return brand === selectedBrand;
      });
      return filtered.length > 0 ? [[selectedBrand, filtered]] : [];
    }
    return categoryGroups.filter(([name]) => name === activeCategory);
  }, [categoryGroups, activeCategory, bestSellers, seasonalFavourites, selectedBrand, inStockProducts, brands]);

  const title = mode === 'products' ? 'Products' : 'Packages';

  return (
    <div className="shop-overlay">
      {/* Header */}
      <div className="shop-header">
        <div className="shop-header-title">{title}</div>
        <button className="shop-header-close" onClick={onClose}>&times;</button>
      </div>

      {/* Category tabs */}
      {mode === 'products' && !loading && inStockProducts.length > 0 && (
        <div className="shop-tabs-wrap">
          <div className="shop-tabs">
            {categoryNames.map(name => (
              <button
                key={name}
                className={`shop-tab${activeCategory === name ? ' active' : ''}`}
                onClick={() => { setActiveCategory(name); if (name !== 'By Brand') setSelectedBrand(''); }}
              >{name}</button>
            ))}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="shop-body">
        {loading ? (
          /* Skeleton loading */
          <div className="shop-product-grid" style={{ padding: '24px' }}>
            {[1,2,3,4].map(i => (
              <div key={i} className="shop-skeleton-card">
                <div className="shop-skeleton-img" />
                <div className="shop-skeleton-text">
                  <div className="shop-skeleton-line short" />
                  <div className="shop-skeleton-line" />
                  <div className="shop-skeleton-line short" />
                </div>
              </div>
            ))}
          </div>
        ) : mode === 'products' ? (
          /* PRODUCTS MODE */
          inStockProducts.length === 0 ? (
            <div className="shop-empty">No products available right now.</div>
          ) : (
            <>
              {/* Curated scroll rows only on "All" tab */}
              {activeCategory === 'All' && (<>
                {newProducts.length > 0 && (
                  <div className="shop-section">
                    <div className="shop-section-title">New In</div>
                    <div className="shop-scroll-row">
                      {newProducts.map(p => (
                        <ProductScrollCard key={p.id} product={p} inBag={isInBag(p.id)} onTap={() => toggleProduct(p)} />
                      ))}
                    </div>
                  </div>
                )}
                {staffPicks.length > 0 && (
                  <div className="shop-section">
                    <div className="shop-section-title">Staff Picks</div>
                    <div className="shop-scroll-row">
                      {staffPicks.map(p => (
                        <ProductScrollCard key={p.id} product={p} inBag={isInBag(p.id)} badge="staffpick" onTap={() => toggleProduct(p)} />
                      ))}
                    </div>
                  </div>
                )}
              </>)}

              {/* Brand dropdown */}
              {activeCategory === 'By Brand' && (
                <div className="shop-brand-picker">
                  <select
                    className="shop-brand-select"
                    value={selectedBrand}
                    onChange={e => setSelectedBrand(e.target.value)}
                  >
                    <option value="">All Brands</option>
                    {brands.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              )}

              {/* Category sections */}
              {filteredGroups.map(([catName, catProducts]) => {
                // Separate standalone products from variant products, and collect variant groups
                const standalone = catProducts.filter(p => !variantIds.has(p.id));
                const seenGroups = new Set();
                const groupsInCategory = [];
                for (const p of catProducts) {
                  if (!variantIds.has(p.id)) continue;
                  for (const [key, group] of variantGroups) {
                    if (!seenGroups.has(key) && group.variants.some(v => v.id === p.id)) {
                      seenGroups.add(key);
                      groupsInCategory.push({ key, ...group });
                    }
                  }
                }

                return (
                  <div key={catName}>
                    {activeCategory === 'All' && (
                      <div className="shop-section">
                        <div className="shop-section-title">{catName}</div>
                      </div>
                    )}
                    <div className="shop-product-grid">
                      {standalone.map(p => {
                        const { brand, productName } = extractBrand(p.name);
                        const imgUrl = getImageUrl(p.image);
                        const inBag = isInBag(p.id);
                        return (
                          <div key={p.id} className={`shop-product-card${inBag ? ' in-bag' : ''}${p.soldOut ? ' sold-out' : ''}`} onClick={() => toggleProduct(p)}>
                            <div className="shop-product-img-wrap">
                              {imgUrl ? (
                                <img className="shop-product-img" src={imgUrl} alt="" onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                              ) : null}
                              <div className="shop-product-img-placeholder" style={imgUrl ? { display: 'none' } : undefined}>
                                {(brand || p.name || '?')[0]}
                              </div>
                              {p.soldOut && <span className="shop-soldout-tag">Sold out</span>}
                            </div>
                            <div className="shop-product-info">
                              {!p.soldOut && p.isNew && <span className="shop-badge shop-badge-new">New</span>}
                              {!p.soldOut && p.isBestSeller && <span className="shop-badge shop-badge-bestseller">Best Seller</span>}
                              {brand && <div className="shop-product-brand">{brand}</div>}
                              <div className="shop-product-name">{productName}</div>
                              <div className="shop-product-price">{formatPrice(p.price)}</div>
                              <div className={`shop-product-cta${inBag ? ' in-bag' : ''}`}>
                                {p.soldOut ? 'Sold out' : inBag ? 'Added ✓ tap to remove' : 'Add to my visit'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {groupsInCategory.map(group => {
                        const imgUrl = getImageUrl(group.image);
                        const anyInBag = group.variants.some(v => isInBag(v.id));
                        return (
                          <div key={group.key} className={`shop-product-card${anyInBag ? ' in-bag' : ''}`} onClick={() => setSelectedVariantGroup(group)}>
                            <div className="shop-product-img-wrap">
                              {imgUrl ? (
                                <img className="shop-product-img" src={imgUrl} alt="" onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                              ) : null}
                              <div className="shop-product-img-placeholder" style={imgUrl ? { display: 'none' } : undefined}>
                                {group.brand[0]}
                              </div>
                            </div>
                            <div className="shop-product-info">
                              <div className="shop-product-brand">{group.brand}</div>
                              <div className="shop-product-name">{group.color}</div>
                              <div className="shop-product-price">{formatPrice(group.price)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )
        ) : (
          /* PACKAGES MODE */
          packages.length === 0 ? (
            <div className="shop-empty">No packages available right now.</div>
          ) : (
            <div style={{ padding: '20px 24px' }}>
              {packages.map(pkg => {
                const inBag = isInBag(pkg.id);
                return (
                  <div key={pkg.id} className={`shop-package-card${inBag ? ' in-bag' : ''}`}>
                    <div className="shop-package-info">
                      <div className="shop-package-name">{pkg.name}</div>
                      {pkg.services?.length > 0 && (
                        <div className="shop-package-services">
                          {pkg.services.map(s => `${s.name}${s.quantity > 1 ? ` x${s.quantity}` : ''}`).join(', ')}
                        </div>
                      )}
                      <div className="shop-package-price">{formatPrice(pkg.priceFinal || pkg.price)}</div>
                    </div>
                    <button
                      className={`shop-add-btn${inBag ? ' in-bag' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (inBag) {
                          onCartRemove(pkg.id);
                        } else {
                          onCartAdd({ id: pkg.id, name: pkg.name, price: pkg.priceFinal || pkg.price, type: 'package' });
                        }
                      }}>
                      {inBag ? 'Remove' : 'Add to Cart'}
                    </button>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Bag bar */}
      <div className="shop-bag-bar">
        {cart.length > 0 && (
          <>
            <div className="shop-bag-summary">
              <span className="shop-bag-count">{cart.length} item{cart.length !== 1 ? 's' : ''} in bag</span>
              <span className="shop-bag-total">{formatPrice(cart.reduce((s, c) => s + (c.price || 0), 0))}</span>
            </div>
            <div className="shop-bag-items">
              {cart.map(item => (
                <span key={item.id} className="shop-bag-item">
                  {item.name.length > 25 ? item.name.slice(0, 25) + '...' : item.name}
                  <button className="shop-bag-item-remove" onClick={() => onCartRemove(item.id)}>&times;</button>
                </span>
              ))}
            </div>
          </>
        )}
        <div className="shop-bag-buttons">
          {cart.length === 0 ? (
            <button className="shop-bag-skip-btn" onClick={onClose}>Close</button>
          ) : (
            <button className="shop-bag-done-btn" onClick={onClose}>Done Shopping</button>
          )}
        </div>
      </div>

      {/* Variant size picker overlay */}
      {selectedVariantGroup && (
        <VariantDetail
          group={selectedVariantGroup}
          isInBag={isInBag}
          onAdd={(variant) => onCartAdd({ id: variant.id, name: variant.product.name, price: variant.product.price, type: 'product', image: variant.product.image || null })}
          // Variants are products too — remove via the invoice-line DELETE path, not shopCart.
          onRemove={(variant) => { if (onProductRemove) onProductRemove(variant.id); }}
          onClose={() => setSelectedVariantGroup(null)}
        />
      )}
    </div>
  );
}

/* Horizontal scroll card for curated rows */
function ProductScrollCard({ product, inBag, badge, onTap }) {
  const { brand, productName } = extractBrand(product.name);
  const imgUrl = getImageUrl(product.image);
  return (
    <div className={`shop-scroll-card shop-product-card${inBag ? ' in-bag' : ''}${product.soldOut ? ' sold-out' : ''}`} onClick={onTap}>
      <div className="shop-product-img-wrap">
        {imgUrl ? (
          <img className="shop-product-img" src={imgUrl} alt="" onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
        ) : null}
        <div className="shop-product-img-placeholder" style={imgUrl ? { display: 'none' } : undefined}>
          {(brand || product.name || '?')[0]}
        </div>
        {product.soldOut && <span className="shop-soldout-tag">Sold out</span>}
      </div>
      <div className="shop-product-info">
        {!product.soldOut && badge === 'bestseller' && <span className="shop-badge shop-badge-bestseller">Best Seller</span>}
        {!product.soldOut && badge === 'staffpick' && <span className="shop-badge shop-badge-staffpick">Staff Pick</span>}
        {!product.soldOut && !badge && product.isNew && <span className="shop-badge shop-badge-new">New</span>}
        {brand && <div className="shop-product-brand">{brand}</div>}
        <div className="shop-product-name">{productName}</div>
        <div className="shop-product-price">{formatPrice(product.price)}</div>
      </div>
    </div>
  );
}

/* Variant size picker slide-up panel (e.g. huha underwear by color) */
function VariantDetail({ group, isInBag, onAdd, onRemove, onClose }) {
  const imgUrl = getImageUrl(group.image);

  return (
    <>
      <div className="shop-detail-backdrop" onClick={onClose} />
      <div className="shop-detail-panel">
        <div className="shop-detail-handle" />
        <button className="shop-detail-close" onClick={onClose}>&times;</button>
        <div className="shop-detail-scroll">
          {imgUrl ? (
            <img className="shop-detail-hero" src={imgUrl} alt="" onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
          ) : null}
          <div className="shop-detail-hero-placeholder" style={imgUrl ? { display: 'none' } : undefined}>
            {group.brand[0]}
          </div>
          <div className="shop-detail-body">
            <div className="shop-detail-brand">{group.brand}</div>
            <div className="shop-detail-name">{group.color}</div>
            <div className="shop-detail-price">{formatPrice(group.price)}</div>
            <div className="shop-variant-label">Select Size</div>
            <div className="shop-variant-sizes">
              {group.variants.map(v => {
                const inBag = isInBag(v.id);
                return (
                  <button
                    key={v.id}
                    className={`shop-variant-size-btn${inBag ? ' in-bag' : ''}`}
                    onClick={() => { inBag ? onRemove(v) : onAdd(v); }}
                  >
                    <span className="shop-variant-size-name">{v.size}</span>
                    {inBag && <span className="shop-variant-size-check">Added</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

